import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import Button from "components/common/Button";
import { StepForm } from "plg/screens/Signup/PlgStepPage";
import SignInErrorNote from "plg/screens/Signup/SignInErrorNote";
import BusinessEmailCard from "plg/screens/Signup/BusinessEmailCard";
import CountryBlockedCard from "plg/screens/Signup/CountryBlockedCard";
import { previewMagicLink, verifyMagicLink } from "plg/api/plgAuth";
import { applyAuthStartResult, ERROR_BY_CODE } from "plg/screens/Signup/authStartResult";
import { trackPlg, trackSignupBlocked } from "plg/analytics/events";
import { endPortalSession } from "plg/auth/portalSession";
import { clearSignupBlocked, rememberSignupBlocked } from "plg/utils/signupBlocked";
import type { SignInError } from "plg/screens/Signup/authStartResult";
import { clearSignupSession } from "plg/utils/signupSession";
import type { ApiResult } from "util/apiError";
import type { MagicLinkPreviewResponse, PlgAuthStartResponse, PlgSignupConfig, SignupAnswers } from "plg/types";
import { PLG_SIGNUP_ROUTE } from "constants/routes";
import "./MagicLink.scss";

interface Props {
  /** Read from ?link=… and already stripped from the address bar by the page. */
  token: string;
  config: PlgSignupConfig | null;
  /** Back to the normal sign-in card (Cloudflare gate first). */
  onRestart: (opts?: { fromBlocked?: boolean }) => void;
}

const EMPTY_SESSION = { prefill: null, enriched: false, answers: {} as SignupAnswers };

/**
 * v2.2: the emailed link opens /signup?link=… . A read-only preview says who the link is for:
 * a new user lands directly on the first sign-up step (the link is used on its Continue), a returning
 * one is signed in straight away. The link works in any browser, but only from a supported country.
 */
function MagicLinkLanding({ token, config, onRestart }: Props) {
  const navigate = useNavigate();
  const [preview, setPreview] = useState<MagicLinkPreviewResponse | null>(null);
  const [error, setError] = useState<SignInError | null>(token ? null : "magicInvalid");
  const previewed = useRef(false);
  const verified = useRef(false);

  useEffect(() => {
    if (!token || previewed.current) return;
    previewed.current = true;
    previewMagicLink({ token }).then((res) => {
      if (res.ok === true) {
        setPreview(res.data);
        if (res.data.mode === "signup") trackPlg("signup_started", { method: "email" });
        // QA ON-1: a personal address goes straight to "We need a business email", before any setup step.
        if (res.data.mode === "blocked") verify();
      } else setError(ERROR_BY_CODE[res.code || ""] || "generic");
    });
  }, [token]);

  const verify = async (): Promise<ApiResult<PlgAuthStartResponse> | null> => {
    if (verified.current) return null;
    verified.current = true;
    clearSignupSession();
    const res = await verifyMagicLink({ token });
    if (res.ok === false) {
      trackSignupBlocked(res.code);
      const err = ERROR_BY_CODE[res.code || ""] || "generic";
      if (err === "blocked") {
        endPortalSession();
        rememberSignupBlocked();
      }
      setError(err);
      if (res.code !== "MAGIC_LINK_INVALID") verified.current = false;
    }
    return res;
  };

  // New user: the first step's Continue verifies the link; StepForm then creates the account with the answers just typed.
  const signupTokenFromLink = async (): Promise<string | null> => {
    const res = await verify();
    if (!res || res.ok === false) return null;
    if (res.data.mode === "login") {
      applyAuthStartResult(res, navigate, config, { replace: true });
      return null;
    }
    endPortalSession();
    clearSignupBlocked();
    return res.data.signupToken;
  };

  const continueLogin = async () => {
    const res = await verify();
    if (!res || res.ok === false) return;
    const err = applyAuthStartResult(res, navigate, config, { replace: true });
    if (err) setError(err);
  };

  // A returning customer needs no extra click: sign in as soon as the preview says so.
  useEffect(() => {
    if (preview?.mode === "login" && config) continueLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, config]);

  if (error === "country") return <CountryBlockedCard />;
  if (error === "blocked") return <BusinessEmailCard onSignUpAgain={() => onRestart({ fromBlocked: true })} />;

  if (error) {
    return (
      <>
        <h1 className="plg-signup__title">Sign in to Edge</h1>
        <SignInErrorNote error={error} screen="magic_link" />
        <div className="plg-step-actions">
          <Button variant="contained" size="large" label="Sign in again" isFullWidth onClick={() => onRestart()} />
        </div>
      </>
    );
  }

  if (!preview || !config || preview.mode === "blocked") return <FormLoader text="Checking your link…" />;

  if (preview.mode === "signup" && config.steps[0]) {
    return <StepForm step={config.steps[0]} config={config} session={EMPTY_SESSION} getSignupToken={signupTokenFromLink} />;
  }

  // A returning customer goes straight in (continueLogin runs once the preview says "login").
  return <FormLoader text="Signing you in…" />;
}

/** The card while the link is checked (feedback 2026-10-26): a spinner in its middle, the step's form replaces it. */
function FormLoader({ text }: { text: string }) {
  return (
    <div className="plg-magic-landing__loader" role="status">
      <CircularProgress size={32} thickness={4} aria-hidden />
      <p>{text}</p>
    </div>
  );
}

export default MagicLinkLanding;
